import { api } from './api.js';
import { esc, fmt$, fmtDate, isOverdue, priorityBadge, closeSheet, deadlineBadge, showToast } from './utils.js';

// ── PLAN TAB ──────────────────────────────────────────────────────────────

let planView      = 'week';   // 'day' | 'week' | 'month' | 'year'
let selectedDay   = todayStr();
let activeSection = 'calendar'; // 'calendar' | 'tasks'
let drawerState   = 'split';    // 'collapsed' | 'split' | 'full' — Day View task drawer only
let planRootEl    = null;       // the element passed to renderPlan() — the drawer's "See all tasks" link needs it to switch activeSection

const DAY_NAMES  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const MON_NAMES  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

// The drawer is a fixed-position element outside the normal tab-panel flow
// (see index.html/#day-drawer) so it stays pinned above the tab bar
// regardless of scroll position — which also means it has to be explicitly
// hidden by anything that isn't Day View, or it would stay visible after
// navigating to a totally different tab. Exported so app.js's switchTab can
// call it when leaving the Plan tab entirely.
export function hideDayDrawer() {
  document.getElementById('day-drawer')?.classList.add('hidden');
}

export async function renderPlan(el) {
  planRootEl = el;
  el.innerHTML = `<div style="display:flex;gap:var(--space-3);margin-bottom:var(--space-4)">
    <button class="btn${activeSection==='calendar'?' btn-primary':' btn-ghost'}" id="plan-cal-tab" style="flex:1;justify-content:center;padding:8px">📅 Calendar</button>
    <button class="btn${activeSection==='tasks'?' btn-primary':' btn-ghost'}" id="plan-tasks-tab" style="flex:1;justify-content:center;padding:8px">✅ Tasks</button>
  </div>
  <div id="plan-section-content"></div>`;

  el.querySelector('#plan-cal-tab').addEventListener('click', () => {
    activeSection = 'calendar';
    renderPlan(el);
  });
  el.querySelector('#plan-tasks-tab').addEventListener('click', () => {
    activeSection = 'tasks';
    renderPlan(el);
  });

  if (activeSection === 'calendar') {
    await renderCalendar(el.querySelector('#plan-section-content'));
  } else {
    hideDayDrawer(); // drawer only makes sense inside Calendar's Day View
    await renderTaskList(el.querySelector('#plan-section-content'));
  }
}

// ── CALENDAR SECTION ─────────────────────────────────────────────────────

async function renderCalendar(el) {
  el.innerHTML = `
    <div class="view-switcher">
      ${['day','week','month','year'].map(v => `
        <button class="view-btn${planView===v?' active':''}" data-view="${v}">
          ${{day:'Day',week:'Week',month:'Month',year:'Year'}[v]}
        </button>`).join('')}
    </div>
    <div id="cal-content"></div>
    <div style="margin-top:var(--space-5);display:flex;gap:var(--space-3)">
      <button class="btn btn-primary" id="add-block-btn" style="flex:1;justify-content:center;padding:10px">+ Add block</button>
    </div>`;

  el.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      planView = btn.dataset.view;
      renderCalendar(el);
    });
  });

  const content = el.querySelector('#cal-content');

  // Load data
  let blocks = [], schoolBlocks = [], tasks = [];
  try {
    // School/recurring blocks are generated on the fly server-side from
    // classes/rules (expandSchoolClasses/expandRecurrenceRules), so the
    // from/to range has to actually cover whatever the active view renders —
    // this used to always be a 7-day window around selectedDay regardless of
    // planView, which meant Month/Year silently never saw school classes or
    // recurring blocks at all (only the plain `blocks` list, itself limited
    // to whatever's been created as literal one-off time_blocks rows).
    const selDate = new Date(selectedDay + 'T12:00:00');
    let rangeFrom, rangeTo;
    if (planView === 'month') {
      // Pad a week on each side so the grid's leading/trailing
      // other-month cells (Month view always fills a whole 7-column grid)
      // still get real data, not just the 1st–last of the anchor month.
      const monthStart = new Date(selDate.getFullYear(), selDate.getMonth(), 1);
      const monthEnd   = new Date(selDate.getFullYear(), selDate.getMonth() + 1, 0);
      monthStart.setDate(monthStart.getDate() - 7);
      monthEnd.setDate(monthEnd.getDate() + 7);
      rangeFrom = monthStart.toISOString().split('T')[0];
      rangeTo   = monthEnd.toISOString().split('T')[0];
    } else if (planView === 'year') {
      // Year View always shows the real current calendar year (it has no
      // year-prev/next control and never reads selectedDay for its own
      // `year` — matching that here rather than selectedDay's year, which
      // could be stale from earlier day/month navigation).
      const y = new Date().getFullYear();
      rangeFrom = `${y}-01-01`;
      rangeTo   = `${y}-12-31`;
    } else {
      // day/week — Monday-Sunday of the selected day's week (unchanged).
      const dowSel = selDate.getDay();
      const monSel = new Date(selDate);
      monSel.setDate(selDate.getDate() - (dowSel === 0 ? 6 : dowSel - 1));
      const sunSel = new Date(monSel); sunSel.setDate(monSel.getDate() + 6);
      rangeFrom = monSel.toISOString().split('T')[0];
      rangeTo   = sunSel.toISOString().split('T')[0];
    }

    let recurringBlocks = [], categories = [];
    [blocks, schoolBlocks, recurringBlocks, tasks, categories] = await Promise.all([
      api.blocks.list(),
      api.blocks.schoolBlocks({ from: rangeFrom, to: rangeTo }),
      api.blocks.recurringBlocks({ from: rangeFrom, to: rangeTo }),
      api.tasks.list({ status: 'open' }),
      api.categories(),
    ]);
    // Materialized rule instances (edited overrides) already come back from api.blocks.list()
    // (they're real time_blocks rows) — drop them here so recurringBlocks (which includes them
    // too, correctly filled in) is the single source of truth and nothing renders twice.
    blocks = blocks.filter(b => !b.ruleId);
    blocks = [...blocks, ...recurringBlocks];

    // Every Day/Week/Month/Year view renders blocks by `.color`, but the API never
    // returns one (time_blocks only stores categoryId) — decorating it here, once,
    // at the single point every view's data flows through, means every renderer
    // that already reads `b.color` starts actually getting a real category color
    // instead of silently falling back to the generic accent color for every block.
    const categoryById = Object.fromEntries(categories.map(c => [c.id, c]));
    blocks = blocks.map(b => ({ ...b, color: b.categoryId ? categoryById[b.categoryId]?.color : undefined }));
  } catch {}

  if (planView === 'day') {
    renderDayView(content, blocks, schoolBlocks, tasks);
  } else {
    hideDayDrawer(); // drawer is Day View-only
    if      (planView === 'week')  renderWeekView(content, blocks, schoolBlocks, tasks);
    else if (planView === 'month') renderMonthView(content, blocks, schoolBlocks, tasks);
    else if (planView === 'year')  renderYearView(content, blocks, schoolBlocks, tasks);
  }

  // Wire add block
  el.querySelector('#add-block-btn').addEventListener('click', () => {
    import('./app.js').then(m => m.openAddBlockSheet({ date: selectedDay }));
  });
}

// ── DAY VIEW ──────────────────────────────────────────────────────────────

function renderDayView(el, blocks, schoolBlocks, tasks) {
  const today = todayStr();

  // Build 7-day strip centered on today
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selectedDay);
    d.setDate(d.getDate() - 3 + i);
    return d.toISOString().split('T')[0];
  });

  el.innerHTML = `
    <div class="day-strip">
      ${days.map(ds => {
        const d   = new Date(ds + 'T12:00:00');
        const num = d.getDate();
        const lbl = DAY_NAMES[d.getDay()];
        const isToday  = ds === today;
        const isActive = ds === selectedDay;
        return `<div class="day-chip${isActive?' active':''}${isToday&&!isActive?' today':''}" data-date="${ds}">
          <span class="day-label">${lbl}</span>
          <span class="day-num">${num}</span>
        </div>`;
      }).join('')}
    </div>

    <div style="margin-bottom:var(--space-4);display:flex;align-items:center;justify-content:space-between">
      <button class="btn btn-ghost" id="day-prev" style="padding:8px">←</button>
      <span style="font-weight:700">${formatDayHeader(selectedDay)}</span>
      <button class="btn btn-ghost" id="day-next" style="padding:8px">→</button>
    </div>

    <div class="time-grid" id="time-grid">
      ${Array.from({ length: 24 }, (_, h) => `
        <div class="time-row" data-hour="${h}">
          <div class="time-label">${h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h-12}pm`}</div>
        </div>`).join('')}
      <div class="current-time-line" id="time-line" style="display:${selectedDay===today?'block':'none'}"></div>
    </div>

    ${tasks.filter(t => t.dueDate === selectedDay || (isOverdue(t.dueDate) && selectedDay === today)).length > 0 ? `
    <div class="card" style="margin-top:var(--space-5)">
      <div class="card-header"><div class="card-title">Due today</div></div>
      ${tasks.filter(t => t.dueDate === selectedDay || (isOverdue(t.dueDate) && selectedDay === today)).map(t => `
        <div class="task-item">
          <div class="task-check" data-tid="${esc(t.id)}"></div>
          <div class="task-body">
            <div class="task-title">${esc(t.title)}</div>
            <div class="task-meta">${priorityBadge(t.priority)}${deadlineBadge(t.dueDate, t.deadlineType)}</div>
          </div>
        </div>`).join('')}
    </div>` : ''}`;

  // Place blocks on time grid
  const dayBlocks = blocks.filter(b => b.date === selectedDay && b.startTime);
  const dayCls    = schoolBlocks.filter(c => c.startTime);

  const allItems = [
    ...dayBlocks.map(b => ({ ...b, isSchool: false })),
    ...dayCls.map(c => ({ ...c, isSchool: true })),
  ];

  const grid = el.querySelector('#time-grid');
  for (const item of allItems) {
    const [sh, sm] = (item.startTime ?? '00:00').split(':').map(Number);
    const [eh, em] = (item.endTime ?? `${sh+1}:00`).split(':').map(Number);
    const startMin = sh * 60 + sm;
    const endMin   = eh * 60 + em;
    const durationMin = Math.max(30, endMin - startMin);

    const block = document.createElement('div');
    block.className = `time-block-el${item.isSchool ? ' school' : ''}`;
    block.style.cssText = `top:${(startMin/60)*60}px;height:${(durationMin/60)*60}px;background:${item.isSchool ? '' : (item.color ? item.color + '33' : 'rgba(var(--accent-rgb),.25)')};border-left:3px solid ${item.color ?? (item.isSchool ? 'var(--cat-school)' : 'var(--accent)')};color:var(--text-primary);cursor:pointer`;
    block.textContent = item.label ?? item.title ?? (item.courseCode ?? '');
    if (!item.isSchool) {
      block.addEventListener('click', () => {
        import('./app.js').then(m => m.openAddBlockSheet(item));
      });
    } else {
      // School blocks: clicking offers to edit in Settings
      block.title = `${item.title} — click to edit in Settings`;
      block.addEventListener('click', () => {
        if (confirm(`${item.title}\n\nTo edit or remove this class, go to Settings → Course Schedule.\n\nGo to Settings now?`)) {
          if (typeof window.switchTab === 'function') window.switchTab('settings');
        }
      });
    }
    grid.appendChild(block);
  }

  // Current time indicator
  if (selectedDay === today) {
    const now = new Date();
    const pct = (now.getHours()*60 + now.getMinutes()) / (24*60);
    const line = el.querySelector('#time-line');
    if (line) line.style.top = `${pct * grid.offsetHeight || pct * 1440}px`;
    // Scroll to current time
    setTimeout(() => {
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const scrollTo = Math.max(0, (nowMin / 60) * 60 - 100);
      el.closest('.tab-panel')?.scrollTo({ top: scrollTo, behavior: 'smooth' });
    }, 100);
  }

  // Day strip navigation
  el.querySelectorAll('.day-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      selectedDay = chip.dataset.date;
      renderDayView(el, blocks, schoolBlocks, tasks);
    });
  });

  el.querySelector('#day-prev').addEventListener('click', () => {
    const d = new Date(selectedDay + 'T12:00:00');
    d.setDate(d.getDate() - 1);
    selectedDay = d.toISOString().split('T')[0];
    renderCalendar(el.closest('#plan-section-content') ?? el.closest('[id]'));
  });
  el.querySelector('#day-next').addEventListener('click', () => {
    const d = new Date(selectedDay + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    selectedDay = d.toISOString().split('T')[0];
    renderCalendar(el.closest('#plan-section-content') ?? el.closest('[id]'));
  });

  // Tap empty time slot → add block
  el.querySelectorAll('.time-row').forEach(row => {
    row.addEventListener('click', (e) => {
      if (e.target !== row && !e.target.classList.contains('time-label')) return;
      const h = parseInt(row.dataset.hour);
      const startTime = `${String(h).padStart(2,'0')}:00`;
      const endTime   = `${String(h+1).padStart(2,'0')}:00`;
      import('./app.js').then(m => m.openAddBlockSheet({ date: selectedDay, startTime, endTime }));
    });
  });

  // Task checkboxes
  el.querySelectorAll('.task-check').forEach(btn => {
    btn.addEventListener('click', async () => {
      btn.classList.add('checked');
      await api.tasks.update(btn.dataset.tid, { status: 'done' }).catch(() => {});
    });
  });

  renderDayDrawer(tasks, blocks);
}

// ── DAY VIEW TASK DRAWER ──────────────────────────────────────────────────
// Matches inspo-images/plan-split-view.png: pinned above the tab bar, an
// "N unscheduled" alert, an "Inbox · N unprocessed → PROCESS" banner distinct
// from the Task List's own Inbox banner, per-task "→ Schedule" buttons
// (reusing the Schedule Assistant), and a few already-scheduled tasks shown
// struck through for context. Three states (collapsed/split/full) set a
// max-height on the fixed container; content scrolls independently inside.
function fmtTimeShort(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`;
}

function renderDayDrawer(tasks, blocks) {
  const drawer = document.getElementById('day-drawer');
  if (!drawer) return;
  drawer.className = `day-drawer ${drawerState}`;

  const blockByTaskId = {};
  for (const b of blocks) if (b.taskId) blockByTaskId[b.taskId] = b;

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const openTasks = tasks.filter(t => t.status === 'open' || t.status === 'in_progress');
  const unscheduled = openTasks
    .filter(t => !blockByTaskId[t.id])
    .sort((a, b) => (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1));
  const scheduled = openTasks.filter(t => blockByTaskId[t.id]);
  const inboxCount = openTasks.filter(t => !t.categoryId).length;

  const priorityDot = p => ({ high: 'var(--negative)', medium: 'var(--caution)', low: 'var(--positive)' }[p] ?? 'var(--text-tertiary)');

  drawer.innerHTML = `
    <div class="day-drawer-handle" id="drawer-handle"></div>
    <div class="day-drawer-header" id="drawer-header">
      <span style="font-size:.85rem;font-weight:700;${unscheduled.length ? 'color:var(--caution)' : 'color:var(--text-secondary)'}">
        ${unscheduled.length ? `⚠ ${unscheduled.length} unscheduled` : '✅ All scheduled'}
      </span>
      <div style="display:flex;gap:6px" id="drawer-state-btns">
        <button class="btn btn-ghost drawer-state-btn" data-state="full" style="padding:4px 10px;font-size:.7rem">↑ FULL</button>
        <button class="btn btn-ghost drawer-state-btn" data-state="collapsed" style="padding:4px 10px;font-size:.7rem">↓ MIN</button>
      </div>
    </div>
    <div class="day-drawer-body">
      ${inboxCount > 0 ? `
      <div class="card card-bordered-accent" id="drawer-inbox-banner" style="padding:var(--space-3);margin-bottom:var(--space-3);cursor:pointer;display:flex;align-items:center;justify-content:space-between">
        <span style="font-size:.8rem">📥 Inbox · <strong>${inboxCount}</strong> unprocessed</span>
        <span style="color:var(--accent);font-size:.75rem;font-weight:700">PROCESS →</span>
      </div>` : ''}

      ${unscheduled.map(t => `
        <div class="card" style="padding:var(--space-3);margin-bottom:var(--space-2)">
          <div style="display:flex;align-items:flex-start;gap:8px">
            <span style="width:8px;height:8px;border-radius:50%;background:${priorityDot(t.priority)};margin-top:5px;flex-shrink:0"></span>
            <div style="flex:1;min-width:0">
              <div style="font-weight:600;font-size:.85rem">${esc(t.title)}</div>
              <div style="font-size:.75rem;color:var(--text-tertiary);margin-top:2px">
                ${t.estimatedHours ? `${t.estimatedHours}h · ` : ''}${t.dueDate ? `Due ${esc(fmtDate(t.dueDate))}` : 'No due date'}
              </div>
              <div style="font-size:.75rem;color:var(--text-tertiary);margin-top:2px">○ Not scheduled</div>
            </div>
            <button class="btn btn-ghost drawer-schedule-btn" data-tid="${esc(t.id)}" style="padding:6px 10px;font-size:.75rem;flex-shrink:0">→ Schedule</button>
          </div>
        </div>`).join('')}

      ${scheduled.slice(0, 3).map(t => {
        const b = blockByTaskId[t.id];
        return `
        <div class="card" style="padding:var(--space-3);margin-bottom:var(--space-2);opacity:.6">
          <div style="display:flex;align-items:flex-start;gap:8px">
            <span style="width:8px;height:8px;border-radius:50%;background:var(--text-tertiary);margin-top:5px;flex-shrink:0"></span>
            <div style="flex:1;min-width:0">
              <div style="font-weight:600;font-size:.85rem;text-decoration:line-through;color:var(--text-tertiary)">${esc(t.title)}</div>
              <div style="font-size:.75rem;color:var(--positive);margin-top:2px">✓ Scheduled${b ? `: ${esc(fmtDate(b.date))}${b.startTime ? ', ' + fmtTimeShort(b.startTime) : ''}` : ''}</div>
            </div>
          </div>
        </div>`;
      }).join('')}

      ${unscheduled.length === 0 && scheduled.length === 0 ? `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No open tasks right now.</p>` : ''}

      <a href="#" id="drawer-see-all" style="display:block;font-size:.8rem;color:var(--accent);margin:var(--space-3) 0;text-align:center">See all tasks →</a>
      <button class="btn btn-primary w-full" id="drawer-add-task" style="justify-content:center;padding:10px">+ Add Task</button>
    </div>`;

  const setState = (s) => {
    drawerState = s;
    drawer.className = `day-drawer ${s}`;
    const fab = document.getElementById('fab');
    if (fab) fab.classList.toggle('hidden', s !== 'collapsed');
  };
  setState(drawerState);

  drawer.querySelectorAll('.drawer-state-btn').forEach(btn => {
    btn.addEventListener('click', (e) => { e.stopPropagation(); setState(btn.dataset.state); });
  });
  // Tapping the handle or header cycles collapsed <-> split for quick access;
  // the two explicit buttons above jump straight to full/collapsed.
  const toggleCollapsed = () => setState(drawerState === 'collapsed' ? 'split' : 'collapsed');
  drawer.querySelector('#drawer-handle').addEventListener('click', toggleCollapsed);
  drawer.querySelector('#drawer-header').addEventListener('click', (e) => {
    if (e.target.closest('.drawer-state-btn')) return;
    toggleCollapsed();
  });

  drawer.querySelector('#drawer-inbox-banner')?.addEventListener('click', () => {
    import('./app.js').then(m => m.openInboxProcessingSheet());
  });
  drawer.querySelectorAll('.drawer-schedule-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const task = tasks.find(t => t.id === btn.dataset.tid);
      if (!task) return;
      if (task.estimatedHours) {
        import('./app.js').then(m => m.openScheduleAssistantSheet(task));
      } else {
        import('./app.js').then(m => m.openAddBlockSheet({ title: task.title, taskId: task.id }));
      }
    });
  });
  drawer.querySelector('#drawer-see-all').addEventListener('click', (e) => {
    e.preventDefault();
    activeSection = 'tasks';
    if (planRootEl) renderPlan(planRootEl);
  });
  drawer.querySelector('#drawer-add-task').addEventListener('click', () => {
    import('./app.js').then(m => m.openAddTaskSheet());
  });
}

// ── WEEK VIEW ─────────────────────────────────────────────────────────────

async function renderWeekView(el, blocks, schoolBlocks, tasks) {
  const today = todayStr();
  const anchor = new Date(selectedDay + 'T12:00:00');
  // Find Monday of this week
  const dow = anchor.getDay();
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() - (dow === 0 ? 6 : dow - 1));

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  const weekStart = weekDays[0];
  const weekEnd   = weekDays[6];

  // Same paydays endpoint Month/Year views already use, plus a pay-period
  // lookup for this specific week (not just "today") so Prev/Next navigation
  // shades whatever week is actually being viewed.
  let paydaySet = new Set(), commissionSet = new Set(), periodStart = null, periodEnd = null, categories = [];
  try {
    const [{ paydays, commissionDays }, period, cats] = await Promise.all([
      api.dashboard.paydays(monday.getFullYear()),
      api.dashboard.payPeriod(weekStart),
      api.categories(),
    ]);
    paydaySet = new Set(paydays);
    commissionSet = new Set(commissionDays);
    periodStart = period.start;
    periodEnd = period.end;
    categories = cats;
  } catch {}
  const categoryById = Object.fromEntries(categories.map(c => [c.id, c]));

  // Category time totals for the week — from user blocks (categoryId'd) plus
  // school classes folded into a "School" pseudo-total, matching the color
  // every other view already uses for school blocks (--cat-school).
  const minutesOf = (b) => {
    if (!b.startTime || !b.endTime) return 0;
    const [sh, sm] = b.startTime.split(':').map(Number);
    const [eh, em] = b.endTime.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    return mins > 0 ? mins : 0;
  };
  const weekBlocks = blocks.filter(b => weekDays.includes(b.date));
  const weekSchool = (schoolBlocks || []).filter(b => weekDays.includes(b.date));
  const totalsByCategory = {};
  for (const b of weekBlocks) {
    const cat = b.categoryId ? categoryById[b.categoryId] : null;
    const key = cat ? cat.id : '__uncategorized';
    totalsByCategory[key] ??= { name: cat ? cat.name : 'Uncategorized', color: cat ? cat.color : 'var(--text-tertiary)', minutes: 0 };
    totalsByCategory[key].minutes += minutesOf(b);
  }
  if (weekSchool.length) {
    totalsByCategory.__school ??= { name: 'School', color: 'var(--cat-school)', minutes: 0 };
    for (const b of weekSchool) totalsByCategory.__school.minutes += minutesOf(b);
  }
  const categoryTotals = Object.values(totalsByCategory).filter(t => t.minutes > 0).sort((a, b) => b.minutes - a.minutes);

  // Tasks due this week — Week View previously received `tasks` but never
  // read it at all, so a task due mid-week was invisible here even though
  // Day View already shows the identical data for a single day.
  const tasksByDay = {};
  for (const t of tasks) {
    if (weekDays.includes(t.dueDate)) (tasksByDay[t.dueDate] ??= []).push(t);
  }

  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--space-4)">
      <button class="btn btn-ghost" id="week-prev" style="padding:8px">← Prev</button>
      <span style="font-weight:700;font-size:.9rem">${fmtDate(weekStart)} – ${fmtDate(weekEnd)}</span>
      <button class="btn btn-ghost" id="week-next" style="padding:8px">Next →</button>
    </div>

    <!-- UX_AUDIT X-1: the Weekly Review's only entry point used to be a Pulse
         banner gated on Sunday evening (dayOfWk 0, hour 17+) — roughly 4% of
         the week, and invisible the rest of it. This is the persistent door;
         the banner stays as promotion, not as the only way in. -->
    <div style="display:flex;justify-content:flex-end;margin-bottom:var(--space-4)">
      <button class="btn btn-ghost" id="week-open-review"
        style="font-size:.85rem;padding:6px 12px">📊 Weekly review</button>
    </div>

    <div class="card" style="overflow-x:auto">
      <div style="display:grid;grid-template-columns:repeat(7,1fr);gap:var(--space-2)">
        ${weekDays.map(ds => {
          const d   = new Date(ds + 'T12:00:00');
          const dayBlocks = blocks.filter(b => b.date === ds);
          const isToday = ds === today;
          const isPayday = paydaySet.has(ds);
          const isCommission = commissionSet.has(ds);
          const inPayPeriod = periodStart && periodEnd && ds >= periodStart && ds <= periodEnd;
          const dayTasks = tasksByDay[ds] ?? [];
          return `
            <div style="text-align:center;padding:var(--space-2);cursor:pointer;border-radius:var(--radius-sm);${inPayPeriod ? 'background:rgba(var(--accent-rgb),.06)' : ''}" class="week-day-col" data-date="${ds}" title="${isCommission ? 'Commission payday' : isPayday ? 'Payday' : ''}">
              <div style="font-size:.65rem;font-weight:600;color:var(--text-tertiary);text-transform:uppercase">${DAY_NAMES[d.getDay()]}</div>
              <div style="font-weight:700;font-size:.9rem;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:4px auto;${isToday?'background:var(--accent);color:#fff':''}">${d.getDate()}</div>
              ${isPayday ? `<div style="font-size:.6rem;color:${isCommission?'var(--caution)':'var(--positive)'};font-weight:700">${isCommission?'💰':'💵'}</div>` : ''}
              ${dayTasks.length ? `<div style="font-size:.6rem;color:var(--accent);font-weight:700">✅ ${dayTasks.length} due</div>` : ''}
              <div style="display:flex;flex-direction:column;gap:2px;margin-top:4px">
                ${dayBlocks.slice(0,4).map(b => `
                  <div style="font-size:.65rem;padding:2px 4px;border-radius:3px;background:${b.color?b.color+'22':'rgba(var(--accent-rgb),.2)'};color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;border-left:2px solid ${b.color??'var(--accent)'}">
                    ${esc(b.label ?? b.title ?? '')}
                  </div>`).join('')}
                ${dayBlocks.length > 4 ? `<div style="font-size:.6rem;color:var(--text-tertiary)">+${dayBlocks.length-4} more</div>` : ''}
              </div>
            </div>`;
        }).join('')}
      </div>
      ${categoryTotals.length ? `
      <div style="display:flex;flex-wrap:wrap;gap:var(--space-3);margin-top:var(--space-3);padding-top:var(--space-3);border-top:1px solid var(--bg-tertiary)">
        ${categoryTotals.map(t => `
          <span style="font-size:.7rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:4px">
            <span style="width:8px;height:8px;border-radius:50%;background:${t.color};display:inline-block"></span>
            ${esc(t.name)} <span class="font-mono" style="color:var(--text-primary)">${fmtHoursMinutes(t.minutes)}</span>
          </span>`).join('')}
      </div>` : ''}
    </div>

    <div class="card" style="margin-top:var(--space-4)">
      <div class="card-header">
        <div class="card-title">This week's schedule</div>
      </div>
      ${weekDays.map(ds => {
        const dayCustom  = blocks.filter(b => b.date === ds);
        const daySchool  = (schoolBlocks || []).filter(b => b.date === ds);
        const allDay     = [
          ...dayCustom.map(b  => ({ ...b, isSchool: false })),
          ...daySchool.map(b  => ({ ...b, isSchool: true })),
        ].sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''));
        const dayTasks = tasksByDay[ds] ?? [];
        if (!allDay.length && !dayTasks.length) return '';
        const d = new Date(ds + 'T12:00:00');
        return `
          <div style="margin-bottom:var(--space-4)">
            <div style="font-size:.75rem;font-weight:700;color:var(--text-secondary);margin-bottom:var(--space-2)">${DAY_NAMES[d.getDay()]} ${d.getDate()}</div>
            ${dayTasks.map(t => `
              <div class="schedule-item">
                <div class="schedule-time">✅</div>
                <div class="schedule-bar" style="background:var(--accent)"></div>
                <div class="schedule-body">
                  <div class="schedule-title">${esc(t.title)}</div>
                  <div class="schedule-sub">${priorityBadge(t.priority)} task due</div>
                </div>
              </div>`).join('')}
            ${allDay.map(b => `
              <div class="schedule-item">
                <div class="schedule-time">${b.startTime ?? '—'}</div>
                <div class="schedule-bar" style="background:${b.isSchool ? 'var(--cat-school)' : (b.color ?? 'var(--accent)')}"></div>
                <div class="schedule-body">
                  <div class="schedule-title">${esc(b.label ?? b.title ?? '')}</div>
                  ${b.isSchool ? '<div class="schedule-sub">class</div>' : (b.location ? `<div class="schedule-sub">${esc(b.location)}</div>` : '')}
                </div>
              </div>`).join('')}
          </div>`;
      }).filter(Boolean).join('') || `<p class="color-tertiary text-sm">No blocks this week.</p>`}
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-4);margin-top:var(--space-4)">
      <!-- Week notes -->
      <div class="card" style="margin:0">
        <div class="card-title" style="margin-bottom:var(--space-2)">Week ahead notes</div>
        <textarea id="week-notes" placeholder="Write objectives, priorities, or notes for the week..." style="width:100%;height:100px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary);padding:8px;font-size:.85rem;resize:none">${esc(localStorage.getItem('week_notes_' + weekStart) ?? '')}</textarea>
        <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:4px;text-align:right" id="notes-status">Auto-saved locally</div>
      </div>
      <!-- Quick additions -->
      <div class="card" style="margin:0;display:flex;flex-direction:column;justify-content:space-between">
        <div>
          <div class="card-title" style="margin-bottom:var(--space-2)">Quick task add</div>
          <form id="week-quick-task-form" style="display:flex;flex-direction:column;gap:var(--space-2)">
            <input type="text" id="qt-title" placeholder="Task title" style="width:100%;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary);padding:8px;font-size:.85rem" required>
            <div style="display:flex;gap:var(--space-2)">
              <select id="qt-priority" style="flex:1;font-size:.8rem;padding:6px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary)">
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
                <option value="low">Low Priority</option>
              </select>
              <input type="date" id="qt-date" value="${weekEnd}" style="flex:1;font-size:.8rem;padding:6px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary)">
            </div>
            <button type="submit" class="btn btn-primary" style="justify-content:center;padding:8px;font-size:.85rem">Add Task</button>
          </form>
        </div>
      </div>
    </div>`;

  // Navigate to day view on cell click
  el.querySelectorAll('.week-day-col').forEach(col => {
    col.addEventListener('click', () => {
      selectedDay = col.dataset.date;
      planView = 'day';
      renderCalendar(el.closest('#plan-section-content') ?? el);
    });
  });

  el.querySelector('#week-open-review')?.addEventListener('click', () => {
    import('./app.js').then(m => m.openWeeklyReviewSheet());
  });

  el.querySelector('#week-prev').addEventListener('click', () => {
    const d = new Date(monday); d.setDate(monday.getDate() - 7);
    selectedDay = d.toISOString().split('T')[0];
    renderCalendar(el.closest('#plan-section-content') ?? el);
  });
  el.querySelector('#week-next').addEventListener('click', () => {
    const d = new Date(monday); d.setDate(monday.getDate() + 7);
    selectedDay = d.toISOString().split('T')[0];
    renderCalendar(el.closest('#plan-section-content') ?? el);
  });

  // Week notes saving
  const notesText = el.querySelector('#week-notes');
  notesText?.addEventListener('input', () => {
    localStorage.setItem('week_notes_' + weekStart, notesText.value);
    const status = el.querySelector('#notes-status');
    if (status) status.textContent = 'Saving...';
  });
  notesText?.addEventListener('blur', () => {
    const status = el.querySelector('#notes-status');
    if (status) status.textContent = 'Saved local draft';
  });

  // Quick task submit
  el.querySelector('#week-quick-task-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const titleVal = el.querySelector('#qt-title').value.trim();
    const priorityVal = el.querySelector('#qt-priority').value;
    const dateVal = el.querySelector('#qt-date').value || undefined;
    if (!titleVal) return;
    try {
      await api.tasks.create({
        title: titleVal,
        priority: priorityVal,
        dueDate: dateVal,
      });
      el.querySelector('#qt-title').value = '';
      showToast('Task added');
      const stateContent = el.closest('#plan-section-content');
      if (stateContent) renderCalendar(stateContent);
    } catch {}
  });
}

// ── MONTH VIEW ────────────────────────────────────────────────────────────

async function renderMonthView(el, blocks, schoolBlocks = [], tasks = []) {
  const anchor = new Date(selectedDay + 'T12:00:00');
  const year   = anchor.getFullYear();
  const month  = anchor.getMonth();
  const today  = todayStr();

  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);

  // Pad start (Mon = 0)
  const startDow = (firstDay.getDay() + 6) % 7;
  const totalCells = Math.ceil((startDow + lastDay.getDate()) / 7) * 7;

  let cells = [];
  for (let i = 0; i < totalCells; i++) {
    const d = new Date(year, month, 1 - startDow + i);
    cells.push(d);
  }

  // .month-cell.payday / .commission (design-system.css) existed with nothing
  // ever applying them — this is that wiring.
  let paydaySet = new Set(), commissionSet = new Set();
  try {
    const { paydays, commissionDays } = await api.dashboard.paydays(year);
    paydaySet = new Set(paydays);
    commissionSet = new Set(commissionDays);
  } catch {}

  // Pay-period-start left border: computed client-side from the same
  // payPeriodAnchor/payPeriodLengthDays settings dashboard.ts's own
  // getPayPeriod() uses server-side, rather than adding a new endpoint for
  // one derived value. School semester markers: real semesterStart/End
  // ranges from the user's actual classes, not a hardcoded date range (Year
  // View's heatmap has one of those, '2026-08-24'..'2026-12-18' — this
  // deliberately doesn't repeat that pattern here).
  let periodStartSet = new Set(), semesterRanges = [];
  try {
    const [settingsData, classes] = await Promise.all([
      api.settings.get().catch(() => null),
      api.blocks.classes.list().catch(() => []),
    ]);
    if (settingsData?.payPeriodAnchor && settingsData?.payPeriodLengthDays) {
      const anchorMs = new Date(settingsData.payPeriodAnchor + 'T12:00:00').getTime();
      const periodMs = settingsData.payPeriodLengthDays * 86_400_000;
      for (const d of cells) {
        const ds = d.toISOString().split('T')[0];
        const dMs = new Date(ds + 'T12:00:00').getTime();
        if ((dMs - anchorMs) % periodMs === 0) {
          periodStartSet.add(ds);
        }
      }
    }
    semesterRanges = [...new Map(classes.map(c => [`${c.semesterStart}_${c.semesterEnd}`, c])).values()]
      .map(c => ({ start: c.semesterStart, end: c.semesterEnd }));
  } catch {}
  const isInSemester = (ds) => semesterRanges.some(r => ds >= r.start && ds <= r.end);

  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--space-4)">
      <button class="btn btn-ghost" id="month-prev" style="padding:8px">←</button>
      <span style="font-weight:700">${MON_NAMES[month]} ${year}</span>
      <button class="btn btn-ghost" id="month-next" style="padding:8px">→</button>
    </div>

    <div class="card">
      <div class="month-grid">
        ${['M','T','W','T','F','S','S'].map(d => `<div class="cal-day-label">${d}</div>`).join('')}
        ${cells.map(d => {
          const ds = d.toISOString().split('T')[0];
          const isThisMonth = d.getMonth() === month;
          const isToday = ds === today;
          const isPayday = paydaySet.has(ds);
          const isCommission = commissionSet.has(ds);
          const isPeriodStart = periodStartSet.has(ds);
          const isSemester = isInSemester(ds);
          const dayCustom = blocks.filter(b => b.date === ds);
          const daySchool = (schoolBlocks || []).filter(b => b.date === ds);
          const dayBlocks = [...dayCustom, ...daySchool.map(b => ({ ...b, color: b.color ?? 'var(--cat-school)' }))];
          const dayTasks  = tasks.filter(t => t.dueDate === ds);
          const cats = [...new Set(dayBlocks.map(b => b.color).filter(Boolean))].slice(0,3);
          const titleExtra = [
            isCommission ? 'commission payday' : isPayday ? 'payday' : null,
            isPeriodStart ? 'pay period starts' : null,
            isSemester ? 'in semester' : null,
            dayTasks.length ? `${dayTasks.length} task${dayTasks.length!==1?'s':''} due` : null,
          ].filter(Boolean).join(', ');
          return `
            <div class="month-cell${isToday?' today':''}${!isThisMonth?' other-month':''}${isPayday?' payday':''}${isCommission?' commission':''}${isPeriodStart?' period-start':''}${isSemester?' semester':''}" data-date="${ds}" title="${esc(ds)}${titleExtra ? ' — ' + esc(titleExtra) : ''}">
              <span class="month-daynum">${d.getDate()}</span>
              ${dayTasks.length ? `<span style="position:absolute;top:2px;right:2px;font-size:.55rem;font-weight:700;color:var(--accent)">✅${dayTasks.length > 1 ? dayTasks.length : ''}</span>` : ''}
              <div class="month-dots">
                ${cats.map(c => `<div class="month-dot" style="background:${c}"></div>`).join('')}
                ${!cats.length && dayBlocks.length ? `<div class="month-dot" style="background:var(--accent)"></div>` : ''}
              </div>
            </div>`;
        }).join('')}
      </div>
    </div>`;

  el.querySelectorAll('.month-cell').forEach(cell => {
    cell.addEventListener('click', () => {
      selectedDay = cell.dataset.date;
      planView = 'day';
      renderCalendar(el.closest('#plan-section-content') ?? el);
    });
  });

  // Go back through renderCalendar (not a direct renderMonthView recursion)
  // so school blocks/tasks get refetched for the newly-selected month's real
  // range — the previous direct-recursion form quietly kept whatever range
  // was fetched for the month the view started on.
  el.querySelector('#month-prev').addEventListener('click', () => {
    const d = new Date(year, month - 1, 1);
    selectedDay = d.toISOString().split('T')[0];
    renderCalendar(el.closest('#plan-section-content') ?? el);
  });
  el.querySelector('#month-next').addEventListener('click', () => {
    const d = new Date(year, month + 1, 1);
    selectedDay = d.toISOString().split('T')[0];
    renderCalendar(el.closest('#plan-section-content') ?? el);
  });
}

// ── YEAR VIEW ─────────────────────────────────────────────────────────────

async function renderYearView(el, blocks, schoolBlocks = [], tasks = []) {
  const today = todayStr();
  const year  = new Date().getFullYear();
  const start = new Date(year, 0, 1);

  // Map activity counts by date — blocks + school classes + tasks due, not
  // just blocks. Blocks-only meant a week full of real school classes and
  // due tasks (visible on Week/Day) rendered as a flat, mostly-empty heatmap
  // here, with no way to tell "genuinely nothing happening" from "school +
  // tasks just aren't counted."
  const countByDate = {};
  const tasksByDate  = {};
  for (const b of blocks) {
    if (b.date?.startsWith(String(year))) {
      countByDate[b.date] = (countByDate[b.date] ?? 0) + 1;
    }
  }
  for (const b of (schoolBlocks || [])) {
    if (b.date?.startsWith(String(year))) {
      countByDate[b.date] = (countByDate[b.date] ?? 0) + 1;
    }
  }
  for (const t of tasks) {
    if (t.dueDate?.startsWith(String(year))) {
      countByDate[t.dueDate] = (countByDate[t.dueDate] ?? 0) + 1;
      tasksByDate[t.dueDate] = (tasksByDate[t.dueDate] ?? 0) + 1;
    }
  }

  // Build 52 weeks of cells starting from Jan 1
  const cells = [];
  for (let week = 0; week < 53; week++) {
    for (let dow = 0; dow < 7; dow++) {
      const d = new Date(start);
      d.setDate(start.getDate() + week * 7 + dow);
      if (d.getFullYear() !== year) { cells.push(null); continue; }
      cells.push(d.toISOString().split('T')[0]);
    }
  }

  const maxCount = Math.max(1, ...Object.values(countByDate));

  // UX_AUDIT F-8: the month names used to sit in their own 6-column button grid
  // ABOVE the scroll container, so they could not line up with the heatmap by
  // construction — measured ~295px from the data they named. The grid is
  // `grid-auto-flow: column` over 7-day columns, so a month's column is simply
  // the index of its first cell divided by 7. Deriving it from `cells` (rather
  // than from a date calculation) means the axis and the heatmap can never
  // disagree: they are computed from the same array.
  const monthColumns = MON_NAMES.map((name, m) => {
    const mm  = String(m + 1).padStart(2, '0');
    const idx = cells.findIndex(ds => ds && ds.slice(5, 7) === mm);
    return idx < 0 ? null : { name, m, col: Math.floor(idx / 7) + 1 };
  }).filter(Boolean);

  // The payday outline was previously wired to `isToday` — a copy-paste
  // artifact, not a payday check (today is only a payday roughly once every
  // two weeks). Same paydays endpoint the Month view now uses.
  let paydaySet = new Set(), commissionSet = new Set();
  try {
    const { paydays, commissionDays } = await api.dashboard.paydays(year);
    paydaySet = new Set(paydays);
    commissionSet = new Set(commissionDays);
  } catch {}

  // Real semesterStart/End ranges from the user's actual classes — this
  // used to be a hardcoded '2026-08-24'..'2026-12-18' literal, a second,
  // silently-divergent source of truth from Month View's own dynamic
  // semesterRanges computation (same data, same shape, just never ported
  // over here).
  let semesterRanges = [];
  try {
    const classes = await api.blocks.classes.list();
    semesterRanges = [...new Map(classes.map(c => [`${c.semesterStart}_${c.semesterEnd}`, c])).values()]
      .map(c => ({ start: c.semesterStart, end: c.semesterEnd }));
  } catch {}
  const isInSemester = (ds) => semesterRanges.some(r => ds >= r.start && ds <= r.end);

  el.innerHTML = `
    <div style="font-weight:700;margin-bottom:var(--space-4)">${year} Activity</div>

    <!-- These are navigation, not axis labels: tapping one opens Month View.
         They previously sat directly above the heatmap with no heading, so they
         read as its x-axis while being ~295px from the months they named
         (UX_AUDIT F-8). The real axis now lives inside the scroll container
         below; this row keeps its own job and says so. Kept as a 6-column
         button grid because these are the touch targets — the axis labels are
         far too narrow (16px column pitch) to be tappable. -->
    <div style="font-size:.7rem;color:var(--text-tertiary);text-transform:uppercase;letter-spacing:.06em;font-weight:600;margin-bottom:var(--space-2)">Jump to month</div>
    <div style="display:grid;grid-template-columns:repeat(6,1fr);gap:6px;margin-bottom:var(--space-4)">
      ${MON_NAMES.map((name, i) => `
        <button class="btn btn-ghost year-month-btn" data-month="${i}" style="padding:6px;font-size:.75rem;justify-content:center">${name}</button>
      `).join('')}
    </div>

    <div class="year-graph">
      <!-- Aligned month axis. Inside .year-graph so it scrolls WITH the grid —
           an axis that scrolls independently of its data is worse than none. -->
      <div class="year-month-axis">
        ${monthColumns.map(({ name, col }) => `
          <span style="grid-column:${col}">${name}</span>
        `).join('')}
      </div>
      <div class="year-grid">
        ${cells.map(ds => {
          if (!ds) return `<div></div>`;
          const count   = countByDate[ds] ?? 0;
          const level   = count === 0 ? 0 : count <= maxCount * .33 ? 1 : count <= maxCount * .66 ? 2 : count < maxCount ? 3 : 4;
          const isToday = ds === today;
          const isSem   = isInSemester(ds);
          const isPayday = paydaySet.has(ds);
          const isCommission = commissionSet.has(ds);
          const taskCount = tasksByDate[ds] ?? 0;
          const titleParts = [
            `${count} item${count!==1?'s':''}`,
            taskCount ? `${taskCount} task${taskCount!==1?'s':''} due` : null,
            isCommission ? 'commission payday' : isPayday ? 'payday' : null,
          ].filter(Boolean).join(' — ');
          return `<div class="year-cell${level>0?` level-${level}`:''}${isSem?' semester':''}${isPayday?' payday':''}${isCommission?' commission':''}${isToday?' today-outline':''}" data-date="${ds}" title="${ds}: ${titleParts}"></div>`;
        }).join('')}
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:var(--space-2);margin-top:var(--space-3);font-size:.75rem;color:var(--text-tertiary)">
      <span>Less</span>
      ${[0,1,2,3,4].map(l => `<div style="width:12px;height:12px;border-radius:2px;background:${l===0?'var(--bg-tertiary)':l===1?'rgba(var(--accent-rgb),.25)':l===2?'rgba(var(--accent-rgb),.5)':l===3?'rgba(var(--accent-rgb),.75)':'var(--accent)'}"></div>`).join('')}
      <span>More</span>
      <span style="margin-left:8px;color:rgba(168,85,247,.7)">■ Semester</span>
      <span style="margin-left:8px;color:var(--positive)">▢ Payday</span>
      <span style="margin-left:8px;color:var(--caution)">▢ Commission</span>
    </div>
    
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-4);margin-top:var(--space-5)">
      <!-- Year Goals -->
      <div class="card" style="margin:0">
        <div class="card-title" style="margin-bottom:var(--space-2)">Annual objectives</div>
        <textarea id="year-goals" placeholder="What are your high-level goals for this year? Click to edit..." style="width:100%;height:130px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);color:var(--text-primary);padding:8px;font-size:.85rem;resize:none">${esc(localStorage.getItem('year_goals_' + year) ?? '')}</textarea>
        <div style="font-size:.7rem;color:var(--text-tertiary);margin-top:4px;text-align:right" id="yg-status">Auto-saved locally</div>
      </div>
      <!-- Fixed milestones this year -->
      <div class="card" style="margin:0;font-size:.85rem">
        <div class="card-title" style="margin-bottom:var(--space-2)">Milestones by Month</div>
        <div style="display:flex;flex-direction:column;gap:6.5px;max-height:140px;overflow-y:auto;padding-right:4px">
          <div><strong style="color:var(--accent)">Jan:</strong> Start Trading Bot conservative backtests</div>
          <div><strong style="color:var(--accent)">May:</strong> Graduation & move pre-planning</div>
          <div><strong style="color:var(--accent)">Jul:</strong> Relocation to new apartment & co-signer resolved</div>
          <div><strong style="color:var(--accent)">Aug:</strong> Fall semester starts (2026-08-24)</div>
          <div><strong style="color:var(--accent)">Oct:</strong> Auto insurance semi-annual review</div>
          <div><strong style="color:var(--accent)">Dec:</strong> Finalize 2026 Roth IRA limits ($7,000)</div>
        </div>
      </div>
    </div>`;

  el.querySelectorAll('.year-month-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const month = parseInt(btn.dataset.month, 10);
      // T12:00:00 (noon), not local midnight — matches this file's
      // established pattern everywhere else (day-prev/next, etc.) for
      // avoiding a UTC-conversion date shift near timezone/DST boundaries.
      selectedDay = new Date(year, month, 1, 12).toISOString().split('T')[0];
      planView = 'month';
      renderCalendar(el.closest('#plan-section-content') ?? el);
    });
  });

  el.querySelectorAll('.year-cell[data-date]').forEach(cell => {
    cell.addEventListener('click', () => {
      selectedDay = cell.dataset.date;
      planView = 'day';
      renderCalendar(el.closest('#plan-section-content') ?? el);
    });
  });

  // Year goals saving
  const ygText = el.querySelector('#year-goals');
  ygText?.addEventListener('input', () => {
    localStorage.setItem('year_goals_' + year, ygText.value);
    const status = el.querySelector('#yg-status');
    if (status) status.textContent = 'Saving...';
  });
  ygText?.addEventListener('blur', () => {
    const status = el.querySelector('#yg-status');
    if (status) status.textContent = 'Saved local draft';
  });
}

// ── TASK LIST SECTION ─────────────────────────────────────────────────────

let taskFilter = 'open';
let taskSort   = 'priority';

async function renderTaskList(el) {
  let tasks = [], blocks = [], categories = [];
  try { [tasks, blocks, categories] = await Promise.all([api.tasks.list(), api.blocks.list(), api.categories()]); } catch {}

  const categoryById = Object.fromEntries(categories.map(c => [c.id, c]));
  const scheduledTaskIds = new Set(blocks.map(b => b.taskId).filter(Boolean));

  const filteredMap = {
    all:       tasks,
    open:      tasks.filter(t => t.status === 'open' || t.status === 'in_progress'),
    high:      tasks.filter(t => t.priority === 'high' && t.status !== 'done'),
    this_week: tasks.filter(t => {
      if (!t.dueDate) return false;
      const d = new Date(t.dueDate + 'T12:00:00');
      const now = new Date();
      const wEnd = new Date(now); wEnd.setDate(now.getDate() + (6 - now.getDay()));
      return d <= wEnd && t.status !== 'done';
    }),
    inbox: tasks.filter(t => !t.categoryId && t.status !== 'done'),
    done: tasks.filter(t => t.status === 'done'),
  };
  const filtered = filteredMap[taskFilter] ?? filteredMap.open;
  const inboxCount = filteredMap.inbox.length;

  const sortFn = (a, b) => {
    if (taskSort === 'priority') {
      const o = { high: 0, medium: 1, low: 2 };
      return (o[a.priority] ?? 1) - (o[b.priority] ?? 1);
    }
    if (taskSort === 'due') {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    }
    if (taskSort === 'created') return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    return a.title.localeCompare(b.title);
  };
  const sorted = [...filtered].sort(sortFn);

  const openCount = tasks.filter(t => t.status === 'open' || t.status === 'in_progress').length;

  // Grouped by priority only makes sense when that's actually the sort order —
  // grouping headers while sorted by due date/A-Z/created would fight the sort.
  const groups = taskSort === 'priority'
    ? [['high','High priority'],['medium','Medium priority'],['low','Low priority']]
        .map(([p, label]) => [label, sorted.filter(t => t.priority === p)])
        .filter(([, rows]) => rows.length > 0)
    : [[null, sorted]];

  el.innerHTML = `
    <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;margin-bottom:var(--space-3)">
      ${[['open','Open'],['all','All'],['high','High'],['this_week','This week'],['inbox','Inbox'],['done','Done']].map(([v,l]) => `
        <button class="btn${taskFilter===v?' btn-primary':' btn-ghost'} task-filter-btn" data-filter="${v}"
          style="padding:6px 12px;font-size:.8rem">${l}</button>`).join('')}
      <button class="btn btn-primary" id="add-task-btn" style="margin-left:auto;padding:6px 16px">+ Task</button>
    </div>

    ${inboxCount > 0 && taskFilter !== 'inbox' ? `
    <div class="card card-bordered-accent inbox-banner" style="padding:var(--space-3) var(--space-4);margin-bottom:var(--space-3);display:flex;align-items:center;justify-content:space-between;gap:var(--space-3)">
      <span class="inbox-banner-filter" style="font-size:.85rem;cursor:pointer">📥 Inbox · <strong>${inboxCount}</strong> unprocessed</span>
      <button class="btn btn-primary inbox-process-btn" style="padding:6px 14px;font-size:.8rem;flex-shrink:0">Process →</button>
    </div>` : ''}

    <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-4)">
      <span style="font-size:.75rem;color:var(--text-tertiary)">${openCount} open</span>
      <div style="flex:1"></div>
      <span style="font-size:.75rem;color:var(--text-tertiary)">Sort:</span>
      <select id="task-sort" style="width:auto;font-size:.8rem;padding:4px 8px">
        <option value="priority" ${taskSort==='priority'?'selected':''}>Priority</option>
        <option value="due"      ${taskSort==='due'?'selected':''}>Due date</option>
        <option value="created"  ${taskSort==='created'?'selected':''}>Created</option>
        <option value="alpha"    ${taskSort==='alpha'?'selected':''}>A–Z</option>
      </select>
    </div>

    ${sorted.length === 0 ? `
      <div style="text-align:center;padding:var(--space-10) 0">
        <div style="font-size:2rem;margin-bottom:var(--space-3)">✅</div>
        <div style="color:var(--text-secondary)">No tasks here.</div>
      </div>` :
      groups.map(([label, rows]) => `
        ${label ? `<div style="font-size:.7rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:var(--text-tertiary);margin:var(--space-3) 0 var(--space-2)">${label}</div>` : ''}
        ${rows.map(t => renderTaskCard(t, scheduledTaskIds.has(t.id), categoryById[t.categoryId])).join('')}
      `).join('')}`;

  // Filter buttons
  el.querySelectorAll('.task-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => { taskFilter = btn.dataset.filter; renderTaskList(el); });
  });
  el.querySelector('.inbox-banner-filter')?.addEventListener('click', () => { taskFilter = 'inbox'; renderTaskList(el); });
  el.querySelector('.inbox-process-btn')?.addEventListener('click', () => {
    import('./app.js').then(m => m.openInboxProcessingSheet());
  });

  // Sort
  el.querySelector('#task-sort').addEventListener('change', (e) => {
    taskSort = e.target.value; renderTaskList(el);
  });

  // Task actions
  el.querySelectorAll('.task-check').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const tid = btn.dataset.tid;
      btn.classList.add('checked');
      await api.tasks.update(tid, { status: 'done' }).catch(() => {});
      setTimeout(() => renderTaskList(el), 300);
    });
  });

  el.querySelectorAll('.task-card').forEach(card => {
    card.addEventListener('click', () => {
      if (card.dataset.suppressClick === 'true') { delete card.dataset.suppressClick; return; }
      const tid = card.dataset.tid;
      const task = tasks.find(t => t.id === tid);
      if (task) import('./app.js').then(m => m.openAddTaskSheet(task));
    });
  });

  // Source links open in a new tab — don't also trigger the card's own
  // click-to-edit behavior.
  el.querySelectorAll('.task-source-link').forEach(link => {
    link.addEventListener('click', (e) => e.stopPropagation());
  });

  // "○ Not scheduled · Schedule →" — same destination as the swipe-right
  // gesture, for anyone who doesn't discover swiping. stopPropagation so it
  // doesn't also trigger the card's click-to-edit.
  el.querySelectorAll('.task-schedule-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const tid = btn.dataset.tid;
      const task = tasks.find(t => t.id === tid);
      if (!task) return;
      if (task.estimatedHours) {
        import('./app.js').then(m => m.openScheduleAssistantSheet(task));
      } else {
        import('./app.js').then(m => m.openAddBlockSheet({ title: task.title, taskId: task.id }));
      }
    });
  });

  // Add task
  el.querySelector('#add-task-btn').addEventListener('click', () => {
    import('./app.js').then(m => m.openAddTaskSheet());
  });

  wireSwipeGestures(el, tasks);
}

// Swipe left = mark done, swipe right = open the Add Block sheet pre-linked
// to this task (mirrors the roadmap's original tap-target spec, using Pointer
// Events so the same code path handles touch and mouse — the latter is what
// Playwright drives in this project's verify scripts).
function wireSwipeGestures(el, tasks) {
  const THRESHOLD = 90; // px of drag before release commits to the action
  const MAX_DRAG  = 120; // clamp so the card never fully leaves its row

  el.querySelectorAll('.task-swipe-wrap').forEach(wrap => {
    const card = wrap.querySelector('.task-card');
    const bg   = wrap.querySelector('.task-swipe-bg');
    const tid  = wrap.dataset.tid;
    let startX = 0, startY = 0, dx = 0, dragging = false, axis = null;

    const reset = (animate) => {
      card.style.transition = animate ? 'transform .2s ease' : 'none';
      card.style.transform = 'translateX(0)';
      bg.style.opacity = '0';
    };

    card.addEventListener('pointerdown', (e) => {
      // Elements with their own click behavior must be excluded here, not
      // just given a stopPropagation in their own handler — once this
      // handler calls setPointerCapture below, the browser retargets the
      // resulting pointerup/click to the CARD, not the original element
      // clicked, so a child's own click listener never fires at all (found
      // live: clicking "Schedule →" opened the edit sheet instead, because
      // the synthetic click landed on the card once capture redirected it).
      if (e.target.closest('.task-check') || e.target.closest('.task-source-link') || e.target.closest('.task-schedule-btn')) return;
      startX = e.clientX; startY = e.clientY; dx = 0; dragging = true; axis = null;
      card.style.transition = 'none';
      // Route subsequent pointermove/pointerup to this card even if the
      // pointer leaves its bounds mid-drag — without this, releasing outside
      // the card never fires pointerup here and leaves it stuck mid-swipe.
      card.setPointerCapture(e.pointerId);
    });

    card.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const rawX = e.clientX - startX;
      const rawY = e.clientY - startY;
      if (axis === null) {
        if (Math.abs(rawX) < 8 && Math.abs(rawY) < 8) return;
        axis = Math.abs(rawX) > Math.abs(rawY) ? 'x' : 'y';
      }
      if (axis !== 'x') return; // vertical scroll — let the page handle it
      e.preventDefault();
      dx = Math.max(-MAX_DRAG, Math.min(MAX_DRAG, rawX));
      card.style.transform = `translateX(${dx}px)`;
      if (dx <= -10) {
        bg.style.opacity = '1';
        bg.style.background = 'var(--positive)';
        bg.style.justifyContent = 'flex-end';
        bg.textContent = '✓ Complete';
      } else if (dx >= 10) {
        bg.style.opacity = '1';
        bg.style.background = 'var(--accent)';
        bg.style.justifyContent = 'flex-start';
        bg.textContent = '📅 Schedule';
      } else {
        bg.style.opacity = '0';
      }
    });

    const onRelease = async () => {
      if (!dragging) return;
      dragging = false;
      // Any decided drag (x OR y, not just one that clears the action
      // threshold) suppresses the following click — mouse input doesn't get
      // the touch-scroll click-suppression browsers apply natively, so a
      // vertical drag (e.g. scrolling the list with a mouse) would otherwise
      // still fire a native click on mouseup and incorrectly open the edit
      // sheet. `axis` is only non-null once the 8px decision threshold is
      // crossed, so a genuine zero-movement tap is unaffected. Confirmed live
      // via Playwright for both the horizontal and vertical cases.
      if (axis !== null) card.dataset.suppressClick = 'true';

      if (axis === 'x' && dx <= -THRESHOLD) {
        card.style.transition = 'transform .15s ease';
        card.style.transform = 'translateX(-100%)';
        await api.tasks.update(tid, { status: 'done' }).catch(() => {});
        setTimeout(() => renderTaskList(el), 180);
        return;
      }
      if (axis === 'x' && dx >= THRESHOLD) {
        const task = tasks.find(t => t.id === tid);
        reset(true);
        // Schedule Assistant needs estimatedHours to know how much time to
        // look for — fall back to the plain manual scheduler for tasks that
        // don't have one yet, rather than showing a dead-end sheet.
        if (task && task.estimatedHours) {
          import('./app.js').then(m => m.openScheduleAssistantSheet(task));
        } else if (task) {
          import('./app.js').then(m => m.openAddBlockSheet({ title: task.title, taskId: task.id }));
        }
        return;
      }
      reset(true);
    };

    card.addEventListener('pointerup', onRelease);
    card.addEventListener('pointercancel', () => { dragging = false; reset(true); });
  });
}

function renderTaskCard(t, scheduled = false, category = null) {
  const sources = t.sources ?? [];
  const swipeable = t.status !== 'done';
  const cardHtml = `<div class="card task-card" data-tid="${esc(t.id)}" style="padding:var(--space-4);cursor:pointer;position:relative;background:var(--bg-secondary);touch-action:pan-y">
    <div style="display:flex;gap:var(--space-3);align-items:flex-start">
      <div class="task-check${t.status==='done'?' checked':''}" data-tid="${esc(t.id)}"></div>
      <div style="flex:1;min-width:0">
        <div class="task-title${t.status==='done'?' color-tertiary':''}" style="${t.status==='done'?'text-decoration:line-through':''}">
          ${esc(t.title)}
        </div>
        <div class="task-meta" style="margin-top:var(--space-2)">
          ${category ? `<span style="font-size:.75rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:4px"><span style="width:8px;height:8px;border-radius:50%;background:${esc(category.color)};display:inline-block"></span>${esc(category.name)}</span>` : ''}
          ${priorityBadge(t.priority)}
          ${t.dueDate ? (t.status === 'done' ? `<span class="task-due">Due ${esc(fmtDate(t.dueDate))}</span>` : deadlineBadge(t.dueDate, t.deadlineType)) : ''}
          ${t.estimatedHours ? `<span style="font-size:.75rem;color:var(--text-tertiary)">${t.estimatedHours}h</span>` : ''}
          ${t.status !== 'done' ? (scheduled
            ? `<span style="font-size:.75rem;color:var(--positive)">✓ Scheduled</span>`
            : `<span class="task-schedule-btn" data-tid="${esc(t.id)}" style="font-size:.75rem;color:var(--accent);font-weight:600">○ Not scheduled · Schedule →</span>`) : ''}
        </div>
        ${sources.length ? `
        <div style="margin-top:var(--space-2);display:flex;flex-wrap:wrap;gap:6px">
          ${sources.map(s => `
            <a href="${esc(s.url)}" target="_blank" rel="noopener noreferrer" class="task-source-link"
              style="font-size:.7rem;color:var(--accent);text-decoration:none;background:var(--bg-tertiary);padding:2px 8px;border-radius:99px">🔗 ${esc(s.label)}</a>`).join('')}
        </div>` : ''}
      </div>
    </div>
  </div>`;

  if (!swipeable) return `<div style="margin-bottom:var(--space-2)">${cardHtml}</div>`;

  // Swipe left = complete, swipe right = schedule. The bg layer sits behind
  // the (opaque) card and is only revealed by however far the card has been
  // dragged — one shared layer suffices since a single gesture only ever
  // reveals one side at a time; its color/label/alignment are set per-drag
  // in wireSwipeGestures() rather than duplicated as two permanent halves.
  return `<div class="task-swipe-wrap" data-tid="${esc(t.id)}" style="position:relative;margin-bottom:var(--space-2);border-radius:var(--radius-lg);overflow:hidden">
    <div class="task-swipe-bg" style="position:absolute;inset:0;display:flex;align-items:center;padding:0 20px;font-weight:700;font-size:.85rem;color:#fff;opacity:0"></div>
    ${cardHtml}
  </div>`;
}

// ── HELPERS ──────────────────────────────────────────────────────────────

function formatDayHeader(ds) {
  const d = new Date(ds + 'T12:00:00');
  return `${DAY_NAMES[d.getDay()]}, ${MON_NAMES[d.getMonth()]} ${d.getDate()}`;
}

function fmtHoursMinutes(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
